import { SupabaseError } from '@supabase/supabase-js';
import { logger } from './logger';
import { isAbortError, isAuthSessionMissingError } from './errorGuards';

// Re-export for convenience
export { isAbortError, isAuthSessionMissingError } from './errorGuards';

/**
 * Error types for better categorization and handling
 */
export enum ErrorType {
  // Connectivity errors
  NETWORK = 'network',
  TIMEOUT = 'timeout',
  OFFLINE = 'offline',

  // Authentication errors
  AUTH_REQUIRED = 'auth_required',
  AUTH_EXPIRED = 'auth_expired',
  AUTHENTICATION = 'authentication', // Added for backwards compatibility

  // Permission errors
  PERMISSION_DENIED = 'permission_denied',
  QUOTA_EXCEEDED = 'quota_exceeded',

  // Resource errors
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',

  // Validation errors
  VALIDATION = 'validation',

  // Service errors
  SERVICE_UNAVAILABLE = 'service_unavailable',

  // Database errors
  DATABASE = 'database',

  // Unknown/fallback error
  UNKNOWN = 'unknown',
}

/**
 * Standardized error structure for the application
 */
export interface AppError {
  message: string;
  type: ErrorType;
  code?: string;
  status?: number;
  context?: Record<string, unknown>;
  original?: unknown;
}

/**
 * Error codes that should trigger a redirect to login
 */
const AUTH_ERROR_CODES = [
  'PGRST301', // JWT role claim invalid
  'PGRST302', // JWT verification error
  '401', // Unauthorized
  'PGRST116', // JWT missing from request
  'PGRST104', // Auth invalid
  'EXPIRED_TOKEN', // Token has expired
  'unauthorized', // Generic unauthorized
  'TokenExpiredError', // Token expired
];

/**
 * Check if the error type should redirect to login
 */
export function shouldRedirectToLogin(errorType: ErrorType): boolean {
  return errorType === ErrorType.AUTH_REQUIRED || errorType === ErrorType.AUTH_EXPIRED;
}

/**
 * Get default error message based on error type
 */
export function getDefaultErrorMessage(errorType: ErrorType): string {
  switch (errorType) {
    case ErrorType.NETWORK:
      return 'Network connection error. Please check your internet connection.';
    case ErrorType.TIMEOUT:
      return 'Request timed out. Please try again later.';
    case ErrorType.OFFLINE:
      return 'You appear to be offline. Changes will be saved when you reconnect.';
    case ErrorType.AUTH_REQUIRED:
      return 'Authentication required. Please sign in to continue.';
    case ErrorType.AUTH_EXPIRED:
      return 'Your session has expired. Please sign in again.';
    case ErrorType.PERMISSION_DENIED:
      return 'You do not have permission to perform this action.';
    case ErrorType.QUOTA_EXCEEDED:
      return 'You have reached your usage limit for this feature.';
    case ErrorType.NOT_FOUND:
      return 'The requested resource was not found.';
    case ErrorType.CONFLICT:
      return 'There was a conflict with your request. Please try again.';
    case ErrorType.VALIDATION:
      return 'Invalid data provided. Please check your input.';
    case ErrorType.SERVICE_UNAVAILABLE:
      return 'Service is temporarily unavailable. Please try again later.';
    case ErrorType.DATABASE:
      return 'Database error occurred. Please try again.';
    case ErrorType.UNKNOWN:
    default:
      return 'An unexpected error occurred. Please try again later.';
  }
}

/**
 * Identifies the type of error based on the error object
 */
function identifyErrorType(error: unknown): ErrorType {
  // Check if it's an auth session missing error (expected for logged-out users)
  if (error instanceof Error && error.name === 'AuthSessionMissingError') {
    return ErrorType.AUTH_REQUIRED;
  }

  // Check if it's a network error
  if (error instanceof Error && error.message.includes('Failed to fetch')) {
    return ErrorType.NETWORK;
  }

  // Check if it's a timeout error
  if (error instanceof Error && error.message.includes('timeout')) {
    return ErrorType.TIMEOUT;
  }

  // Handle Supabase errors
  if (isSupabaseError(error)) {
    const code = error.code || error.status?.toString() || '';

    // Auth errors
    if (AUTH_ERROR_CODES.includes(code)) {
      return ErrorType.AUTH_EXPIRED;
    }

    // Permission errors
    if (code === 'PGRST105' || code === '403') {
      return ErrorType.PERMISSION_DENIED;
    }

    // Not found errors
    if (code === '404' || code === 'PGRST204') {
      return ErrorType.NOT_FOUND;
    }

    // Database errors
    if (code.startsWith('PGRST') || code.startsWith('23')) {
      return ErrorType.DATABASE;
    }
  }

  // Navigator offline check
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return ErrorType.OFFLINE;
  }

  // Default to unknown error
  return ErrorType.UNKNOWN;
}

/**
 * Gets the error type from an unknown error
 * This is a wrapper around identifyErrorType to make it available externally
 */
export function getErrorType(error: unknown): ErrorType {
  return identifyErrorType(error);
}

/**
 * Type guard for Supabase errors
 */
function isSupabaseError(error: unknown): error is SupabaseError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('code' in error || 'status' in error || 'message' in error)
  );
}

/**
 * Extracts status code from an error
 */
function extractStatusCode(error: unknown): number | undefined {
  if (isSupabaseError(error) && error.status) {
    return typeof error.status === 'number' ? error.status : undefined;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return error.status;
  }

  return undefined;
}

/**
 * Extracts error code from an error
 */
function extractErrorCode(error: unknown): string | undefined {
  if (isSupabaseError(error) && error.code) {
    return error.code.toString();
  }

  if (typeof error === 'object' && error !== null && 'code' in error && error.code !== null) {
    return String(error.code);
  }

  return undefined;
}

/**
 * Extracts error message from an error
 */
function extractErrorMessage(error: unknown): string {
  // If it's an Error object
  if (error instanceof Error) {
    return error.message;
  }

  // If it's a Supabase error
  if (isSupabaseError(error) && error.message) {
    return error.message;
  }

  // If it's an object with a message property
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  // If it's a string
  if (typeof error === 'string') {
    return error;
  }

  // Default error message
  return 'An unknown error occurred';
}

/**
 * Safely serializes an error for logging (Error objects don't serialize well with JSON.stringify)
 */
function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack?.split('\n').slice(0, 5).join('\n'), // Limit stack trace
      // Include any additional properties from Supabase/custom errors
      ...(isSupabaseError(error) && {
        code: error.code,
        status: error.status,
        details: (error as Record<string, unknown>).details,
        hint: (error as Record<string, unknown>).hint,
      }),
    };
  }

  if (typeof error === 'object' && error !== null) {
    try {
      // Try to serialize the object, but catch circular reference errors
      return JSON.parse(JSON.stringify(error));
    } catch {
      return { type: 'unserializable', toString: String(error) };
    }
  }

  return { value: String(error) };
}

/**
 * Creates a standardized AppError from any error type
 */
export function createAppError(error: unknown, context?: Record<string, unknown>): AppError {
  // Skip logging for expected/benign errors
  const shouldSkipLogging = isAuthSessionMissingError(error) || isAbortError(error);

  // Extract error details before logging to ensure proper serialization
  const errorType = identifyErrorType(error);
  const message = extractErrorMessage(error);
  const code = extractErrorCode(error);
  const status = extractStatusCode(error);

  if (!shouldSkipLogging) {
    // Log the error with properly serialized error details
    logger.error('Error occurred in application', {
      error: serializeError(error),
      errorMessage: message,
      errorType,
      errorCode: code,
      context,
    });
  }

  const appError: AppError = {
    message,
    type: errorType,
    code,
    status,
    context,
    original: error,
  };

  return appError;
}

/**
 * Retry configuration for different error types
 */
export interface RetryConfig {
  maxRetries: number;
  retryDelay: (attemptIndex: number) => number;
  shouldRetry: (error: Error, attemptIndex: number) => boolean;
}

/**
 * Default retry function for React Query
 * @param attemptIndex The current attempt index (0-based)
 * @param error The error that occurred
 */
export function defaultRetryFunction(attemptIndex: number, error: Error): boolean {
  // Convert to AppError to identify type
  const appError = createAppError(error);

  // Error types that should not be retried
  const nonRetryableErrors = [
    ErrorType.AUTH_EXPIRED,
    ErrorType.AUTH_REQUIRED,
    ErrorType.PERMISSION_DENIED,
    ErrorType.VALIDATION,
    ErrorType.NOT_FOUND,
  ];

  // Don't retry these errors
  if (nonRetryableErrors.includes(appError.type)) {
    return false;
  }

  // Retry network/timeout errors up to 3 times
  if (appError.type === ErrorType.NETWORK || appError.type === ErrorType.TIMEOUT) {
    return attemptIndex < 3;
  }

  // Retry service unavailable up to 5 times
  if (appError.type === ErrorType.SERVICE_UNAVAILABLE) {
    return attemptIndex < 5;
  }

  // Default retry behavior for other errors (up to 2 times)
  return attemptIndex < 2;
}

/**
 * Calculate exponential backoff delay with jitter
 * @param attemptIndex The current attempt index (0-based)
 */
export function getExponentialBackoff(attemptIndex: number): number {
  const baseDelay = 1000; // 1 second
  const maxDelay = 30000; // 30 seconds

  // Calculate exponential delay: 2^attemptIndex * baseDelay
  const delay = Math.min(Math.pow(2, attemptIndex) * baseDelay, maxDelay);

  // Add randomness (jitter) to prevent thundering herd
  const jitter = Math.random() * 0.25 * delay;

  return delay + jitter;
}

/**
 * Creates custom retry configuration based on error type
 */
export function createRetryConfig(options?: Partial<RetryConfig>): RetryConfig {
  return {
    maxRetries: options?.maxRetries ?? 3,
    retryDelay: options?.retryDelay ?? getExponentialBackoff,
    shouldRetry: options?.shouldRetry ?? defaultRetryFunction,
  };
}

/**
 * Determines if an error type is retryable
 * Retryable errors are typically transient and might resolve on retry
 */
export function isRetryableError(errorType: ErrorType): boolean {
  const retryableErrors = [
    ErrorType.NETWORK,
    ErrorType.TIMEOUT,
    ErrorType.SERVICE_UNAVAILABLE,
    ErrorType.DATABASE,
    ErrorType.UNKNOWN,
  ];

  return retryableErrors.includes(errorType);
}
